import type { GltfLowMemoryReasonCode, MeshFormat } from '@/plugins/mesh/types'

/** 静态 Mesh 错误基类。 */
export class StaticMeshError extends Error {
  constructor(
    message: string,
    readonly source?: string,
    options?: ErrorOptions
  ) {
    super(message, options)
    this.name = 'StaticMeshError'
  }
}

/** 没有 Loader 能识别请求来源。 */
export class MeshFormatNotSupportedError extends StaticMeshError {
  constructor(
    source: string,
    readonly registeredFormats: readonly string[]
  ) {
    super(
      `不支持静态 Mesh 来源 "${source}"，已注册格式: ${
        registeredFormats.length > 0 ? registeredFormats.join(', ') : '(无)'
      }`,
      source
    )
    this.name = 'MeshFormatNotSupportedError'
  }
}

/** 格式解析或规范化失败。 */
export class MeshAssetLoadError extends StaticMeshError {
  constructor(
    readonly format: MeshFormat,
    source: string,
    cause: unknown
  ) {
    const reason = cause instanceof Error ? cause.message : String(cause)
    super(`加载 ${format} 静态 Mesh 失败，来源 "${source}": ${reason}`, source, { cause })
    this.name = 'MeshAssetLoadError'
  }
}

/** PLY 静态 Mesh 校验失败的稳定原因码。 */
export const PLY_MESH_VALIDATION_CODES = [
  'invalid-header',
  'missing-vertices',
  'point-data-only',
  'gaussian-splat',
  'missing-face-indices',
  'invalid-topology',
] as const

/** PLY 静态 Mesh 校验失败原因。 */
export type PlyMeshValidationCode = (typeof PLY_MESH_VALIDATION_CODES)[number]

/** PLY 可解析，但不满足静态 Mesh 的 Header 或三角面拓扑约束。 */
export class PlyMeshValidationError extends StaticMeshError {
  constructor(
    source: string,
    readonly code: PlyMeshValidationCode,
    detail: string
  ) {
    super(`PLY 不是有效的静态 Mesh，来源 "${source}": ${detail}`, source)
    this.name = 'PlyMeshValidationError'
  }
}

/** 首版静态 Mesh 路径拒绝蒙皮资产。 */
export class SkinnedMeshNotSupportedError extends StaticMeshError {
  constructor(
    source: string,
    readonly nodeName?: string
  ) {
    super(`静态 Mesh 不支持蒙皮对象${nodeName ? ` "${nodeName}"` : ''}，来源 "${source}"`, source)
    this.name = 'SkinnedMeshNotSupportedError'
  }
}

/** 加载任务被取消。 */
export class MeshLoadCancelledError extends StaticMeshError {
  constructor(source: string, options?: ErrorOptions) {
    super(`静态 Mesh 加载已取消，来源 "${source}"`, source, options)
    this.name = 'AbortError'
  }
}

/** glTF 低内存路径错误基类，携带稳定原因码。 */
export class GltfLowMemoryError extends StaticMeshError {
  constructor(
    message: string,
    readonly code: GltfLowMemoryReasonCode,
    source: string,
    options?: ErrorOptions
  ) {
    super(message, source, options)
    this.name = 'GltfLowMemoryError'
  }
}

/** glTF JSON 或 GLB 容器不满足受控解析契约。 */
export class InvalidGltfManifestError extends GltfLowMemoryError {
  constructor(source: string, detail: string, cause?: unknown) {
    super(`glTF manifest 无效，来源 "${source}": ${detail}`, 'invalid-manifest', source, {
      cause,
    })
    this.name = 'InvalidGltfManifestError'
  }
}

/** glTF manifest 中的数组索引或字节范围越界。 */
export class GltfReferenceOutOfBoundsError extends GltfLowMemoryError {
  constructor(
    source: string,
    readonly reference: string,
    cause?: unknown
  ) {
    super(`glTF 引用越界，来源 "${source}": ${reference}`, 'invalid-reference', source, { cause })
    this.name = 'GltfReferenceOutOfBoundsError'
  }
}

/** 资产结构无法由低内存白名单路径完整表达。 */
export class GltfLowMemoryUnsupportedError extends GltfLowMemoryError {
  constructor(
    source: string,
    code: Extract<
      GltfLowMemoryReasonCode,
      | 'unsupported-extension'
      | 'unsupported-skin'
      | 'unsupported-morph-target'
      | 'unsupported-sparse-accessor'
      | 'unsupported-attribute'
      | 'unsupported-primitive-mode'
      | 'unsupported-material-extension'
    >,
    readonly feature: string,
    cause?: unknown
  ) {
    super(`低内存 glTF 路径不支持 "${feature}"，来源 "${source}"`, code, source, {
      cause,
    })
    this.name = 'GltfLowMemoryUnsupportedError'
  }
}

/** 低内存计划或实际分配超过配置的硬预算。 */
export class GltfMemoryBudgetExceededError extends GltfLowMemoryError {
  constructor(
    source: string,
    code: Extract<
      GltfLowMemoryReasonCode,
      | 'budget-final-exceeded'
      | 'budget-transient-exceeded'
      | 'budget-batch-exceeded'
      | 'budget-range-cache-exceeded'
      | 'budget-texture-exceeded'
      | 'full-buffer-budget-exceeded'
    >,
    readonly actualBytes: number,
    readonly limitBytes: number,
    cause?: unknown
  ) {
    super(
      `低内存 glTF 预算超限，来源 "${source}"，实际: ${actualBytes}，上限: ${limitBytes}`,
      code,
      source,
      { cause }
    )
    this.name = 'GltfMemoryBudgetExceededError'
  }
}

/** HTTP Range 探测或响应校验失败。 */
export class GltfRangeRequestError extends GltfLowMemoryError {
  constructor(
    source: string,
    code: Extract<GltfLowMemoryReasonCode, 'range-unavailable' | 'range-invalid-response'>,
    detail: string,
    cause?: unknown
  ) {
    super(`glTF Range 读取失败，来源 "${source}": ${detail}`, code, source, { cause })
    this.name = 'GltfRangeRequestError'
  }
}

/** 直接 Geometry 构建失败。 */
export class GltfDirectBuildError extends GltfLowMemoryError {
  constructor(source: string, detail: string, cause?: unknown) {
    super(`低内存 glTF 直接构建失败，来源 "${source}": ${detail}`, 'direct-build-failed', source, {
      cause,
    })
    this.name = 'GltfDirectBuildError'
  }
}

/** 若信号已取消则抛出统一的 Mesh 取消错误。 */
export function throwIfMeshLoadCancelled(signal: AbortSignal | undefined, source: string): void {
  if (signal?.aborted) {
    throw new MeshLoadCancelledError(source, { cause: signal.reason })
  }
}
